import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useStore } from "react-redux"
import { useNavigate } from "react-router"
import { NetworkApi, OrderApi } from "../../apis"
import { addToCart, updateQuantity, type CartItem, type ICart } from "../../store/Cart"
import { renderBusinessName } from "../../utils/render-business-name"
import { message } from "../../utils/message"
import { translateBackendError } from "../delivery/translate-error"
import { networkCanOrder } from "./product-list/cart-model"

// «Дахин захиалах» (B26): an order's lines go back into the buyer cart in the
// shape the catalogue puts them there (product-list/index.tsx addToCart), so the
// cart drawer, the re-pricing and the checkout treat them like any other line.

/** The order's seller: a PURCHASE order goes to its receiver, a SALES order comes from its sender. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const orderSupplier = (order: any) => (order?.type === "SALES" ? order?.senderBusiness : order?.receiverBusiness)

/** Promotion gifts are added by the price engine, never ordered by hand. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isGiftLine = (line: any) => line?.priceGroupName === "FREE_GOODS" && !!line?.promotionId

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function orderLinesToCart(order: any): CartItem[] {
  const supplier = orderSupplier(order)
  const supplierBusinessId = supplier?.id || (order?.type === "SALES" ? order?.senderBusinessId : order?.receiverBusinessId)
  if (!supplierBusinessId) return []
  const supplierName = renderBusinessName(supplier, "")
  return (order?.lines || [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .filter((line: any) => !!line?.variantId && Number(line?.quantity) > 0 && !isGiftLine(line))
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((line: any) => ({
      id: line.variantId,
      variantId: line.variantId,
      variantParentId: line.variantParentId || line.variantId,
      supplierBusinessId,
      supplierName,
      price: Number(line.price) || 0,
      quantity: Number(line.quantity) || 1,
      nameMon: line.name,
      skuCode: line.skuCode,
      image: line.image,
      unit: line.unit,
      vatType: line.vatType,
      vatPercent: line.vatPercent,
      inventories: line.warehouseId ? [{ warehouseId: line.warehouseId }] : [],
    }))
}

/**
 * Why an order cannot be re-ordered, or null when it can. Only decidable once the
 * order's lines are loaded (the detail page); the list checks after fetching.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function reorderBlockedReason(order: any, t: (key: string) => string, network?: any): string | null {
  if (!order) return null
  if (order.consignmentAgreementId) return t("order:buyerOrders.reorderConsignment")
  // B40: an inactive network with the supplier takes no orders (GET /ord/network/:id canOrder).
  if (network && !networkCanOrder(network)) return t("order:buyerCustomer.cannotOrderInactive")
  if (Array.isArray(order.lines) && orderLinesToCart(order).length === 0) return t("order:buyerOrders.reorderNoLines")
  return null
}

/** The order's supplier business id (the id `GET /ord/network/:id` takes). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const orderSupplierId = (order: any): string =>
  orderSupplier(order)?.id || (order?.type === "SALES" ? order?.senderBusinessId : order?.receiverBusinessId) || ""

/** Puts an order's lines into the cart and opens the catalogue (`/buyer-sales/new`). */
export function useReorder() {
  const { t } = useTranslation(["order", "common"])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const store = useStore<{ cart: ICart }>()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addOrder = useCallback(async (order: any) => {
    const supplierId = orderSupplierId(order)
    // A network that fails to load is not refused here; checkout checks it again.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const network: any = supplierId ? await NetworkApi.get(supplierId).catch(() => null) : null
    const blocked = reorderBlockedReason(order, t, network)
    const lines = orderLinesToCart(order)
    if (blocked || lines.length === 0) {
      message.error(blocked || t("order:buyerOrders.reorderNoLines"))
      return
    }
    // Read the cart as it is now, after the awaits, not a render-time snapshot.
    lines.forEach((line) => {
      const existing = store
        .getState()
        .cart.items.find((i) => i.id === line.id && i.supplierBusinessId === line.supplierBusinessId)
      if (existing) {
        dispatch(updateQuantity({ productId: line.id, supplierBusinessId: line.supplierBusinessId, quantity: existing.quantity + line.quantity }))
      } else {
        dispatch(addToCart(line))
      }
    })
    message.success(t("order:buyerOrders.reorderDone", { count: lines.length }))
    navigate("/buyer-sales/new")
  }, [store, dispatch, navigate, t])

  /** List rows carry no lines: load the order first. */
  const reorderById = useCallback(async (id: string) => {
    try {
      const order = await OrderApi.get(id)
      await addOrder(order)
    } catch (err) {
      message.error(translateBackendError(err))
    }
  }, [addOrder])

  return { addOrder, reorderById }
}
